import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Handshake, PlusCircle, RotateCcw, Loader2 } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";

const REASONS = [
  { code: "repair", label: "Repair replacement" },
  { code: "onboarding", label: "Onboarding gap" },
  { code: "travel", label: "Travel loaner" },
  { code: "temporary", label: "Temporary assignment" },
  { code: "other", label: "Other" },
];

export default function Loaners() {
  const [pool, setPool] = useState({ items: [], total: 0, available: 0, on_loan: 0 });
  const [loans, setLoans] = useState([]);
  const [statusFilter, setStatusFilter] = useState("on_loan");
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [showPool, setShowPool] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (statusFilter === "overdue") { delete params.status; params.overdue_only = true; }
    Promise.all([
      api.get("/loaners/pool"),
      api.get("/loaners", { params }),
    ]).then(([p, l]) => {
      setPool(p.data);
      setLoans(l.data.items || []);
    }).finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load, statusFilter]);

  const doReturn = async (id) => {
    const cond = window.prompt("Condition on return (A/B/C/D):", "A");
    if (!cond) return;
    try {
      await api.post(`/loaners/${id}/return`, { condition_in: cond.toUpperCase() });
      toast.success("Loaner returned");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Lifecycle · Advanced</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Loaner Pool</h1>
          <p className="text-sm text-slate-500 mt-0.5">Short-term devices for repair, travel and onboarding gaps.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showPool} onOpenChange={setShowPool}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="loaner-manage-pool-btn"><Handshake className="h-4 w-4 mr-1.5" /> Manage pool</Button>
            </DialogTrigger>
            <ManagePoolDialog onDone={() => { setShowPool(false); load(); }} />
          </Dialog>
          <Dialog open={showIssue} onOpenChange={setShowIssue}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900" data-testid="loaner-issue-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> Issue loaner</Button>
            </DialogTrigger>
            <IssueLoanerDialog pool={pool} onDone={() => { setShowIssue(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SummaryTile label="Pool size" value={pool.total} tone="slate" />
        <SummaryTile label="Available" value={pool.available} tone="green" />
        <SummaryTile label="On loan" value={pool.on_loan} tone="blue" />
        <SummaryTile
          label="Overdue"
          value={loans.filter((l) => l.is_overdue).length}
          tone="rose"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-800">Loan activity</div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="loaner-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="on_loan">On loan</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Code</th>
              <th className="text-left px-4 py-2.5">Device</th>
              <th className="text-left px-4 py-2.5">Employee</th>
              <th className="text-left px-4 py-2.5">Reason</th>
              <th className="text-left px-4 py-2.5">Issued / Expected return</th>
              <th className="text-left px-4 py-2.5">Condition</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : loans.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No loan records</td></tr>
            ) : loans.map((r) => (
              <tr key={r.id} data-testid={`loaner-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{r.loan_code}</td>
                <td className="px-4 py-2.5">
                  {r.asset ? (<><div className="font-medium text-slate-800">{r.asset.brand} {r.asset.model}</div><div className="text-xs text-slate-500 font-mono">{r.asset.serial}</div></>) : "—"}
                </td>
                <td className="px-4 py-2.5">
                  {r.employee ? (<><div className="text-slate-800">{r.employee.name}</div><div className="text-xs text-slate-500">{r.employee.email}</div></>) : "—"}
                </td>
                <td className="px-4 py-2.5 capitalize text-slate-700">{r.reason?.replace(/_/g, " ")}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600">
                  <div>{new Date(r.issued_at).toLocaleDateString()}</div>
                  <div className={r.is_overdue ? "text-rose-600 font-medium" : "text-slate-400"}>
                    → {new Date(r.expected_return).toLocaleDateString()}
                    {r.is_overdue && <span className="ml-1"> · overdue</span>}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs">
                  <div>Out: <StatusBadge value={r.condition_out} /></div>
                  {r.condition_in && <div className="mt-1">In: <StatusBadge value={r.condition_in} /></div>}
                </td>
                <td className="px-4 py-2.5">
                  <StatusBadge value={r.status} tone={r.is_overdue ? "red" : undefined} />
                </td>
                <td className="px-4 py-2.5 text-right">
                  {r.status === "on_loan" && (
                    <Button size="sm" variant="outline" onClick={() => doReturn(r.id)} data-testid={`loaner-return-${r.id}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Return
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone = "slate" }) {
  const map = {
    slate: "text-slate-900",
    green: "text-emerald-700",
    blue: "text-sky-700",
    rose: "text-rose-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-heading font-bold tabular-nums ${map[tone]}`}>{value}</div>
    </div>
  );
}

function ManagePoolDialog({ onDone }) {
  const [q, setQ] = useState("");
  const [assets, setAssets] = useState([]);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/assets/", { params: { q: q || undefined, limit: 25 } })
      .then(({ data }) => setAssets(data.items || []));
  }, [q]);

  const toggle = async (a) => {
    setBusy((b) => ({ ...b, [a.id]: true }));
    try {
      await api.post("/loaners/pool", { asset_id: a.id, in_pool: !a.is_loaner });
      setAssets((list) => list.map((x) => x.id === a.id ? { ...x, is_loaner: !a.is_loaner } : x));
      toast.success(`${!a.is_loaner ? "Added to" : "Removed from"} pool`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy((b) => ({ ...b, [a.id]: false })); }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Manage loaner pool</DialogTitle></DialogHeader>
      <Input placeholder="Search asset…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
      <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
        {assets.map((a) => (
          <div key={a.id} className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800">{a.brand} {a.model}</div>
              <div className="text-xs text-slate-500 font-mono">{a.asset_tag} · {a.serial}</div>
            </div>
            {a.is_loaner ? <StatusBadge value="in pool" tone="blue" /> : null}
            <Button size="sm" variant={a.is_loaner ? "outline" : "default"} className={!a.is_loaner ? "bg-slate-900" : ""} disabled={!!busy[a.id]} onClick={() => toggle(a)}>
              {a.is_loaner ? "Remove" : "Add to pool"}
            </Button>
          </div>
        ))}
      </div>
      <DialogFooter><Button onClick={onDone} variant="outline">Close</Button></DialogFooter>
    </DialogContent>
  );
}

function IssueLoanerDialog({ pool, onDone }) {
  const [emps, setEmps] = useState([]);
  const [empQuery, setEmpQuery] = useState("");
  const available = (pool.items || []).filter((a) => !a.current_employee_id && ["in_warehouse", "available", "ready_to_deploy"].includes(a.lifecycle_state));
  const [form, setForm] = useState({ asset_id: "", employee_id: "", reason: "repair", expected_return: "", condition_out: "A", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/employees", { params: { q: empQuery || undefined, limit: 15 } })
      .then(({ data }) => setEmps(data.items || []));
  }, [empQuery]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/loaners/issue", form);
      toast.success("Loaner issued");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  const valid = form.asset_id && form.employee_id && form.expected_return;

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Issue loaner device</DialogTitle></DialogHeader>
      <div className="space-y-4">
        {available.length === 0 && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div>No available loaner devices in the pool. Use &ldquo;Manage pool&rdquo; to add loaner-eligible assets first.</div>
          </div>
        )}
        <div>
          <Label className="text-xs">Loaner asset (from pool)</Label>
          <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
            <SelectTrigger className="h-10 mt-1" data-testid="loaner-asset-select"><SelectValue placeholder="Select loaner asset…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {available.map((a) => <SelectItem key={a.id} value={a.id}>{a.brand} {a.model} · {a.serial}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Employee</Label>
          <Input placeholder="Search employee…" value={empQuery} onChange={(e) => setEmpQuery(e.target.value)} className="mt-1 h-9 mb-1.5" />
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger className="h-10" data-testid="loaner-employee-select"><SelectValue placeholder="Select employee…" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
              <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Expected return</Label>
            <Input type="date" className="mt-1 h-10" value={form.expected_return} onChange={(e) => setForm({ ...form, expected_return: e.target.value })} data-testid="loaner-expected-return" />
          </div>
          <div>
            <Label className="text-xs">Condition out</Label>
            <Select value={form.condition_out} onValueChange={(v) => setForm({ ...form, condition_out: v })}>
              <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["A", "B", "C", "D"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button className="bg-slate-900" disabled={!valid || submitting} onClick={submit} data-testid="loaner-submit-btn">
          {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Handshake className="h-4 w-4 mr-1.5" />}
          Issue loaner
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
